/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/lib/constants/departments.ts
 * DESCRIPTION: Canonical list of organization departments.
 *              Used for intra-org data segregation (visibility scoping).
 *              Mirrors the INDUSTRY_SECTORS pattern: stable IDs + labels.
 * 
 * NAMING CONVENTION:
 *   All department IDs use the `dept_` prefix for unambiguous
 *   identification and clean alphabetical grouping.
 * 
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-09-04
 * ─────────────────────────────────────────────────────────────────────
 */

export const ORG_DEPARTMENTS = [
  { id: 'dept_cyber',       label: 'Cybersecurity' },
  { id: 'dept_fraud',       label: 'Fraud & Financial Crime' },
  { id: 'dept_hr',          label: 'Human Resources' },
  { id: 'dept_legal',       label: 'Legal & Compliance' },
  { id: 'dept_it',          label: 'IT & Infrastructure' },
  { id: 'dept_marketing',   label: 'Marketing & Communications' },
  { id: 'dept_executive',   label: 'Executive Leadership' },
  { id: 'dept_operations',  label: 'Operations' },
  { id: 'dept_general',     label: 'General / Other' },
] as const;

export type DepartmentId = typeof ORG_DEPARTMENTS[number]['id'];

/**
 * Helper: look up a department label by its ID.
 * Returns the raw ID if no match is found (defensive).
 */
export const getDepartmentLabel = (id: string): string => {
  const dept = ORG_DEPARTMENTS.find(d => d.id === id);
  return dept ? dept.label : id;
};
